/**
 * @file care-action.schema.ts
 * @description Ações que uma regra do builder de cuidado pode disparar.
 *
 * União discriminada por `type` — como `ButtonActionSchema`, e pelo mesmo
 * motivo: o switch exaustivo no motor e no app quebra o build quando alguém
 * adiciona uma variante sem tratá-la em todos os pontos.
 *
 * PONTOS QUE PRECISAM ACOMPANHAR UMA AÇÃO NOVA:
 *   1. packages/api/src/services/care/care-engine.service.ts   (executa)
 *   2. packages/admin/src/widgets/care-builder/ActionField.tsx (configura)
 *   3. packages/mobile/components/care/ResultScreen.tsx        (renderiza, se visível)
 *
 * Regra de ouro herdada da especificação clínica: `RECOMMEND_*` NÃO interrompe
 * o fluxo. As recomendações são acumuladas durante o wizard e exibidas juntas
 * no fechamento — a usuária não sai no meio para assistir um vídeo.
 */

import { z } from 'zod';

export const CareAlertSeverityEnum = z.enum(['INFO', 'ATTENTION', 'URGENT']);
export type CareAlertSeverity = z.infer<typeof CareAlertSeverityEnum>;

/** Revela um bloco marcado como `hiddenByDefault`. É o skip logic do wizard. */
const ShowBlockActionSchema = z.object({
  type: z.literal('SHOW_BLOCK'),
  blockId: z.string(),
});

/**
 * Grava/atualiza variável de escopo USER ou SESSION.
 * `fromAnswer` grava o valor respondido; senão grava `value` literal.
 * Nunca aponta para variável nativa (`origin: SYSTEM`) — o motor recusa, e o
 * editor não oferece. Prontuário é fonte, não destino (decisão de 29/08/2026).
 */
const SetVariableActionSchema = z.object({
  type: z.literal('SET_VARIABLE'),
  name: z.string().min(1),
  value: z.any().optional(),
  fromAnswer: z.boolean().optional().default(false),
});

/**
 * Aponta curso → módulo → aula. O endereçamento por índice acompanha como o
 * app navega hoje (`/course?courseId=X&moduleIndex=N&classIndex=M`), porque
 * `Section.id` e `MediaItem.id` são opcionais no ContentSchema.
 */
const RecommendContentActionSchema = z.object({
  type: z.literal('RECOMMEND_CONTENT'),
  courseId: z.string(),
  moduleIndex: z.number().int().min(0).optional(),
  classIndex: z.number().int().min(0).optional(),
  /** Rótulo opcional para a tela de resultado. Sem ele, o app usa o título do conteúdo. */
  label: z.string().optional(),
});

/** Sugere uma ferramenta do app (mobilograma, controle de pressão, ...). */
const RecommendToolActionSchema = z.object({
  type: z.literal('RECOMMEND_TOOL'),
  toolKey: z.string().min(1),
  label: z.string().optional(),
});

/** Dispara outro checkpoint (ex.: sofrimento emocional → PHQ-9). */
const ActivateItemActionSchema = z.object({
  type: z.literal('ACTIVATE_ITEM'),
  itemId: z.string(),
});

/** Sinaliza situação de atenção para a equipe. Vira registro auditável. */
const InternalAlertActionSchema = z.object({
  type: z.literal('INTERNAL_ALERT'),
  severity: CareAlertSeverityEnum,
  note: z.string().optional(),
});

/** Dispara e-mail transacional (fase 3 — escores de saúde mental). */
const SendEmailActionSchema = z.object({
  type: z.literal('SEND_EMAIL'),
  templateKey: z.string().min(1),
});

/**
 * Exibe um texto de fechamento condicional — a "mensagem por faixa" dos
 * instrumentos (PHQ-9 0–4, 5–9, ...). Separado de `RECOMMEND_*` porque é
 * conteúdo próprio, não ponteiro para catálogo.
 */
const ShowResultMessageActionSchema = z.object({
  type: z.literal('SHOW_RESULT_MESSAGE'),
  title: z.string().optional(),
  body: z.string(),
  /** Destaque visual. `ATTENTION` puxa o bloco para o topo do resultado. */
  severity: CareAlertSeverityEnum.default('INFO'),
});

/** Agenda um lembrete a partir de uma data respondida (fase 2). */
const CreateReminderActionSchema = z.object({
  type: z.literal('CREATE_REMINDER'),
  reminderItemId: z.string(),
  /** Bloco de data que ancora os offsets. */
  anchorBlockId: z.string().optional(),
});

/** Encerra um lembrete recorrente (fase 2). */
const CloseReminderActionSchema = z.object({
  type: z.literal('CLOSE_REMINDER'),
  reminderItemId: z.string(),
});

/** Envia item para o repositório de exames (fase 2). */
const AddToRepositoryActionSchema = z.object({
  type: z.literal('ADD_TO_REPOSITORY'),
  examType: z.string().optional(),
});

export const CareActionSchema = z.discriminatedUnion('type', [
  ShowBlockActionSchema,
  SetVariableActionSchema,
  RecommendContentActionSchema,
  RecommendToolActionSchema,
  ActivateItemActionSchema,
  InternalAlertActionSchema,
  SendEmailActionSchema,
  ShowResultMessageActionSchema,
  CreateReminderActionSchema,
  CloseReminderActionSchema,
  AddToRepositoryActionSchema,
]);

export type CareAction = z.infer<typeof CareActionSchema>;
export type CareActionType = CareAction['type'];

export {
  ShowBlockActionSchema,
  SetVariableActionSchema,
  RecommendContentActionSchema,
  RecommendToolActionSchema,
  ActivateItemActionSchema,
  InternalAlertActionSchema,
  SendEmailActionSchema,
  ShowResultMessageActionSchema,
  CreateReminderActionSchema,
  CloseReminderActionSchema,
  AddToRepositoryActionSchema,
};
